<template>
  <div>
    <div>
      <naj-search-box
        :type-id="formFieldType.text"
        :placeholder="local.bankruptcy.home.filterWithRequestNo"
        :search.sync="searchQuery.requestCode"
        @filterDataClicked="filterData()"
        @showFilterClicked="toggleFilter()"
      ></naj-search-box>
    </div>
    <div
      v-if="hasFilter"
      class="filter-row-wrapper d-flex mt-3"
    >
      <div class="col-12">
        <template v-for="filter in filters">
          <span
            v-if="searchQuery[filter.name]"
            :key="filter.name"
            class="ma-1"
          >
            <v-chip
              :key="filter.name"
              :input-value="searchQuery[filter.name]"
              color="#F7FBFB"
              close
              outlined
              @click:close="searchQuery[filter.name]=null ; filterData();"
            >
              <strong class="text-primary">{{ filter.label + ':' + (searchQuery[filter.name+'DisplayName'] || searchQuery[filter.name]) }}</strong>
            </v-chip>
          </span>
        </template>
        <span class="ma-1">
          <v-chip
            color="#F7FBFB"
            outlined
          >
            <strong class="text-primary">{{ local.common.searchResults + ':' + pagination.totalCount }}</strong>
          </v-chip>
        </span>
      </div>
    </div>
    <v-row class="mt-7">
      <v-col :cols="showFilter?9:12">
        <naj-item-table
          :data-items="data.list"
          :headers="headers"
          :pagination="pagination"
          :loading="loading"
          :items-per-page="pagination.pageSize"
          :server-items-length="pagination.totalCount"
          :enable-expand="false"
          @update:page="onPageChange"
        >
        </naj-item-table>
      </v-col>
      <v-col
        v-if="showFilter"
        cols="3"
      >
        <v-card color="pa-3">
          <v-row>
            <v-col class="pt-0">
              <div class="text-left">
                <v-icon
                  class="icon"
                  @click="toggleFilter()"
                >
                  mdi-close
                </v-icon>
              </div>
              <div class="text-h4 font-weight-bold">
                {{ local.common.filter }}
              </div>
            </v-col>
            <v-col
              v-for="filter in filters"
              :key="filter.name"
              cols="12"
            >
              <naj-input
                :type-id="filter.typeId"
                :label="filter.label"
                :value.sync="searchQuery[filter.name]"
                :value-name.sync="searchQuery[filter.name+'DisplayName']"
                :items="filter.items"
                item-text="name"
                item-value="id"
              ></naj-input>
            </v-col>

            <v-col
              cols="12"
              class="text-center"
            >
              <naj-button
                :loading="loading"
                @click="filterData()"
              >
                {{ local.common.filter }}
              </naj-button>

              <naj-button
                outlined
                @click="toggleFilter()"
              >
                {{ local.common.close }}
              </naj-button>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
const { mapActions: mapBankruptcyActions, mapState: mapBankruptcyState } = createNamespacedHelpers('BankruptcyModule');
import { toHijriDate } from '@/helpers/utils';
import { procedureTypeEnum } from '@/modules/bankruptcy/helpers/enum';

export default {
  name: 'MainRequestsTab',
  components: {},
  data() {
    return {
      procedureTypeEnum,
      toHijriDate,
      selectedTab: null,
      searchQuery: {
        requestCode: null,
        fromDate: null,
        toDate: null,
        categoryId: null,
        procedureTypeId: null,
        statusId: null,
        legalCapacityId: null,
        openedProcedureTypeId: null,
      },
      loading: false,
      headers: [
        {
          value: 'requestCode',
          text: this.local.bankruptcy.home.headers.requestCode,
          to: (item) => {
            return (item.showBankruptcyFile ? '/bankruptcy/bankruptcy-file/' : '/bankruptcy/request/1/') + item.objectKey;
          },
        },
        {
          value: 'requestDate',
          text: this.local.bankruptcy.home.headers.requestDate,
          formatter: toHijriDate,
        },
        {
          value: 'legalCapacityName',
          text: this.local.bankruptcy.home.headers.legalCapacityName,
        },
        {
          value: 'requestOwnerTypeName',
          text: this.local.bankruptcy.home.headers.onBehalfOf,
        },
        {
          value: 'requestCategoryName',
          text: this.local.bankruptcy.home.headers.requestCategoryName,
        },
        {
          value: 'procedureName',
          text: this.local.bankruptcy.home.headers.procedureName,
        },
        {
          value: 'requestStatusName',
          text: this.local.bankruptcy.home.headers.requestStatusName,
        },
        {
          value: 'openedProcedureName',
          text: this.local.bankruptcy.home.headers.openedProcedureName,
        },
      ],
      data: { list: [] },
      showFilter: false,
      pagination: {
        pageSize: 20,
        pageIndex: 0,
        totalCount: 0,
        totalPages: 0,
        hasPreviousPage: false,
        hasNextPage: false,
      },
    };
  },
  computed: {
    ...mapBankruptcyState([
      'mainRequestCategories',
      'mainRequestStatuses',
      'requestOwnerTypes',
      'legalCapacities',
      'procedureTypes',
      'currentUser',
    ]),
    hasFilter() {
      return this.searchQuery.requestCode ||
        this.searchQuery.toDate ||
        this.searchQuery.fromDate ||
        this.searchQuery.categoryId ||
        this.searchQuery.legalCapacityId ||
        this.searchQuery.statusId ||
        this.searchQuery.procedureTypeId ||
        this.searchQuery.openedProcedureTypeId;
    },
    filters() {
      return [
        this.mapFilterItem('categoryId', 'mainRequestCategory', 'selectbox', this.mainRequestCategories),
        this.mapFilterItem('statusId', 'status', 'selectbox', this.mainRequestStatuses),
        this.mapFilterItem('procedureTypeId', 'procedureName', 'selectbox', this.allowedProcedureTypes),
        this.mapFilterItem('legalCapacityId', 'legalCapacityName', 'selectbox', this.legalCapacities),
        this.mapFilterItem('openedProcedureTypeId', 'openedProcedureName', 'selectbox', this.procedureTypes),
        this.mapFilterItem('fromDate', 'fromDate', 'date'),
        this.mapFilterItem('toDate', 'toDate', 'date'),
      ];
    },
    computedSearchQuery() {
      return Object.assign({}, this.searchQuery);
    },

    allowedProcedureTypes() {
      if (this.currentUser.isBankruptcyTrustee) {
        return this.procedureTypes.filter(pt => pt.id == procedureTypeEnum.administrativeLiquidation);
      }
      return this.procedureTypes.filter(x => x.id != procedureTypeEnum.smallDebtorPreventiveSettlement);
    },
  },
  watch: {
    computedSearchQuery: {
      deep: true,
      handler(newValue, oldValue) {
        const oldValueString = JSON.stringify({
          ...oldValue,
          invoiceNo: null,
          isPaid: null,
        });
        const newValueString = JSON.stringify({
          ...newValue,
          invoiceNo: null,
          isPaid: null,
        });
        const isChanged = oldValueString != newValueString;
        if (isChanged) {
          this.filterData();
        }
      },
    },
  },
  mounted() { this.init(); },
  methods: {
    ...mapBankruptcyActions([
      'getMyMainRequests',
      'getMainRequestCategories',
      'getRequestOwnerTypes',
      'getLegalCapacities',
      'getProcedureTypes',
      'getMainRequestStatuses',
    ]),
    changeTab(tab) {
      this.searchQuery.isPaid = tab.isPaid;
      this.filterData();
    },
    mapFilterItem(name, key, typeId, items = []) {
      return {
        name: name,
        label: this.local.bankruptcy.home.filter[key],
        typeId: this.formFieldType[typeId],
        items,
      };
    },
    toggleFilter() {
      this.showFilter = !this.showFilter;
    },
    init() {
      this.filterData();
      this.getMainRequestCategories();
      this.getRequestOwnerTypes();
      this.getLegalCapacities();
      this.getProcedureTypes();
      this.getMainRequestStatuses();
    },
    loadData() {
      this.loading = true;
      this.getMyMainRequests({
        ...this.searchQuery,
        ...this.pagination,
      })
        .then(({ data }) => {
          this.loading = false;
          this.data = { ...data };
          this.pagination = {
            pageIndex: data.pageIndex,
            pageSize: data.pageSize,
            totalCount: data.totalCount,
            totalPages: data.totalPages,
            hasPreviousPage: data.hasPreviousPage,
            hasNextPage: data.hasNextPage,
          };
        })
        .finally(() => {
          this.loading = false;
        });
    },
    filterData() {
      this.pagination.pageIndex = 0;
      this.loadData();
    },
    onPageChange(pageIndex) {
      this.pagination.pageIndex = pageIndex - 1;
      this.loadData();
    },
  },
};
</script>

<style lang="scss" scoped>
.tab-padding {
  font-size: 24px;
  padding-bottom: 20px;
  color: var(--app-dark-primary-color) !important;
  .v-chip {
    cursor: pointer;
    color: #ffffff;
    background: var(--app-dark-primary-color) !important;
    border-radius: 0px !important;
  }
}
    .tab-active {
        color: var(--app-primary-color) !important;
        font-weight: bold;

        .v-chip {
            background: var(--app-lighter-primary-color) !important;
        }
    }

::v-deep .v-tabs {
  .v-tabs-bar {
    .v-tabs-slider-wrapper {
      height: 5px !important;
    }
  }
}

::v-deep .List__empty {
  margin-top: 0px !important;
}

::v-deep .info-item-box .info-item-details:nth-child(1) {
  background: var(--app-light-secondary-color) 0% 0% no-repeat padding-box;
}

::v-deep .action-item {
  justify-content: flex-end;
}

::v-deep .empty {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding: 0 !important;
  .empty-title {
    font-size: 20px;
    line-height: 43px;
    margin: 0;
  }
}

.search-box {
  border: 2px solid #e5e9f2;
  ::v-deep .v-input.v-text-field--outlined fieldset {
    border-width: 0 !important;
  }
  ::v-deep .v-input.v-text-field--outlined {
    margin-top: 0 !important;
    .v-input__slot {
      margin-bottom: 0px;
    }
    .v-text-field__details {
      display: none;
    }
  }
}

.filter-row-wrapper {
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 10px 30px #d1d5df80;
}

    ::v-deep .v-chip.v-chip--outlined.v-chip.v-chip {
        background-color: #f7fbfb !important;
        border: 1px solid var(--app-primary-color) !important;
        border-radius: unset;

        .v-chip__content {
            button {
                color: var(--app-primary-color);
            }
        }
    }
</style>
